/**
 * Dashboard v2.0 — 5-zone cockpit layout.
 * Zone 0: StatusBar (3-second summary)
 * Zone 1: AlertsSection (critical/warning alerts)
 * Zone 2: KPI TrendCards (4) + PillRow (6 social pills)
 * Zone 3: FunnelMini + SocialTrendsChart (side-by-side)
 * Zone 4: SuggestionsSection (ops + product)
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDashboardAll } from "../api/admin-dashboard";
import type { DashboardData, KpiCard } from "../api/admin-dashboard";
import { AlertsSection } from "../components/AlertsSection";
import { FunnelMini } from "../components/FunnelMini";
import { PageHeader } from "../components/PageHeader";
import { PillRow } from "../components/PillRow";
import { SocialTrendsChart } from "../components/SocialTrendsChart";
import { StatusBar } from "../components/StatusBar";
import { SuggestionsSection } from "../components/SuggestionsSection";
import { TrendCard } from "../components/TrendCard";
import { t } from "../i18n";
import * as shared from "../styles/shared";

// ─── Main Component ───

export function DashboardPage(): React.JSX.Element {
  const { data, error, loading, refresh } = useDashboardData();
  const navigate = useNavigate();

  return (
    <div>
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
        actions={
          <button type="button" onClick={refresh} style={shared.refreshBtn}>
            {t("common.refresh")}
          </button>
        }
      />
      {error && <p style={shared.errorBanner}>{error}</p>}
      {loading && !data && <KPISkeletons />}
      {data && <Zone0StatusBar data={data} />}
      {data && <Zone1Alerts data={data} />}
      {data && <Zone2Vitals data={data} navigate={navigate} />}
      {data && <Zone3Charts data={data} navigate={navigate} />}
      <SuggestionsSection />
    </div>
  );
}

// ─── Zone 0: Status Summary ───

function Zone0StatusBar({
  data,
}: {
  data: DashboardData;
}): React.JSX.Element {
  const alertCount = data.activeAlerts.length;
  const anomalousCount = data.kpiCards.filter(
    (c) => c.anomaly_level !== "normal",
  ).length;
  const normalCount = data.kpiCards.filter(
    (c) => c.anomaly_level === "normal",
  ).length;

  return (
    <section style={{ marginBottom: 16 }}>
      <StatusBar
        alertCount={alertCount}
        anomalousKpiCount={anomalousCount}
        normalKpiCount={normalCount}
        onClick={() =>
          document
            .getElementById("alerts-section")
            ?.scrollIntoView({ behavior: "smooth" })
        }
      />
    </section>
  );
}

// ─── Zone 1: Alerts ───

function Zone1Alerts({
  data,
}: {
  data: DashboardData;
}): React.JSX.Element {
  return (
    <section id="alerts-section" style={{ marginBottom: 24 }}>
      <AlertsSection alerts={data.activeAlerts} />
    </section>
  );
}

// ─── Zone 2: Platform Vitals ───

const KPI_NAV: Record<string, string> = {
  new_users: "/metrics?tab=Growth&highlight=new_users",
  new_agents: "/governance/agents",
  dau: "/metrics?tab=Growth&highlight=dau",
  mrr: "/metrics?tab=Revenue&highlight=mrr",
};

function trendDirection(pct: number): "up" | "down" | "flat" {
  if (pct > 0) return "up";
  if (pct < 0) return "down";
  return "flat";
}

function Zone2Vitals({
  data,
  navigate,
}: {
  data: DashboardData;
  navigate: (path: string) => void;
}): React.JSX.Element {
  const pills = data.socialPills;
  const pillItems = [
    {
      label: "H2A",
      value: pills.h2a_conversations,
      onClick: () => navigate("/metrics?tab=Messages"),
    },
    {
      label: "H2H",
      value: pills.h2h_conversations,
      onClick: () => navigate("/metrics?tab=Messages"),
    },
    {
      label: t("dashboard.group_label_short"),
      value: pills.group_conversations,
      onClick: () => navigate("/metrics?tab=Messages"),
    },
    {
      label: "AgentMsg",
      value: `${pills.agent_msg_percent}`,
      suffix: "%",
      onClick: () => navigate("/metrics?tab=Messages"),
    },
    {
      label: t("dashboard.paid_users"),
      value: pills.paid_users,
      onClick: () => navigate("/revenue/billing"),
    },
    {
      label: "Agents",
      value: pills.total_agents,
      onClick: () => navigate("/governance/agents"),
    },
  ];

  return (
    <section style={shared.section}>
      <div style={s.kpiGrid}>
        {data.kpiCards.map((card: KpiCard) => (
          <TrendCard
            key={card.key}
            label={t(`dashboard.kpi_${card.key}`)}
            value={card.value.toLocaleString()}
            trend={{
              percent: Math.abs(card.change_percent),
              direction: trendDirection(card.change_percent),
            }}
            sparkline={card.sparkline_14d}
            anomalyLevel={card.anomaly_level}
            prevValue={card.prev_value.toLocaleString()}
            onClick={
              KPI_NAV[card.key]
                ? () => {
                    const path = KPI_NAV[card.key];
                    if (path) navigate(path);
                  }
                : undefined
            }
          />
        ))}
      </div>
      <PillRow items={pillItems} />
    </section>
  );
}

// ─── Zone 3: Funnel + Social Trends ───

function Zone3Charts({
  data,
  navigate,
}: {
  data: DashboardData;
  navigate: (path: string) => void;
}): React.JSX.Element {
  const hasFunnel = data.funnelSummary.length > 0;
  const hasTrends = data.socialTrends.length > 0;

  if (!hasFunnel && !hasTrends) return <></>;

  return (
    <section style={shared.section}>
      <div style={s.zone3Grid}>
        <div style={shared.card}>
          <h3 style={s.chartTitle}>{t("dashboard.funnel_title")}</h3>
          <FunnelMini
            steps={data.funnelSummary}
            onStepClick={(step) => navigate(`/metrics?tab=Funnel&step=${step}`)}
          />
        </div>
        <div style={shared.card}>
          <SocialTrendsChart data={data.socialTrends} />
        </div>
      </div>
    </section>
  );
}

// ─── Skeletons ───

function KPISkeletons(): React.JSX.Element {
  return (
    <section style={shared.section}>
      <div style={s.kpiGrid}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} style={shared.skeletonCard}>
            <div style={{ ...shared.skeleton, width: 80, height: 14 }} />
            <div style={{ ...shared.skeleton, width: 60, height: 32 }} />
            <div style={{ ...shared.skeleton, width: "100%", height: 40 }} />
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Data Hook ───

const CACHE_KEY = "hashee_admin_dashboard_v2";
const CACHE_TTL_MS = 60_000;

type CacheEntry = { data: DashboardData; ts: number };

function readCache(): DashboardData | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry;
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
    return entry.data;
  } catch {
    return null;
  }
}

function writeCache(data: DashboardData): void {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* quota exceeded */
  }
}

function useDashboardData(): {
  data: DashboardData | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
} {
  const [data, setData] = useState<DashboardData | null>(readCache);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!readCache());

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDashboardAll();
      setData(result);
      writeCache(result);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, error, loading, refresh: load };
}

// ─── Styles ───

const s: Record<string, React.CSSProperties> = {
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 16,
  },
  zone3Grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 12,
    padding: "0 4px",
  },
};
